import React, { useEffect, useState } from 'react';
import { Modal } from '@/components/primitives/Modal';
import { Button } from '@/components/primitives/Button';
import { Icon } from '@/components/primitives/Icon';
import { updateCampaign } from '@/services/campaigns';
import type { Campaign } from '@/types/api';

interface EditCampaignDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: Campaign | null;
  onSaved?: (campaign: Campaign) => void;
}

const OBJECTIVE_OPTIONS = [
  { value: 'brand-awareness', label: 'Brand Awareness' },
  { value: 'consideration', label: 'Consideration' },
  { value: 'driving-purchase', label: 'Driving Purchase' },
];

export function EditCampaignDetailsModal({
  isOpen,
  onClose,
  campaign,
  onSaved,
}: EditCampaignDetailsModalProps) {
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [objective, setObjective] = useState(OBJECTIVE_OPTIONS[0].value);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setName(campaign?.name ?? '');
    setStartDate('');
    setEndDate('');
    setObjective(OBJECTIVE_OPTIONS[0].value);
    setError('');
  }, [isOpen, campaign?.id, campaign?.name]);

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleSave = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!campaign?.id) return;
    if (!name.trim()) {
      setError('Campaign name is required.');
      return;
    }
    setError('');
    setIsSubmitting(true);
    try {
      const updated = await updateCampaign(campaign.id, { name: name.trim() });
      onSaved?.(updated);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update campaign');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Campaign Details"
      footer={
        <div className="aspectly:flex aspectly:items-center aspectly:justify-end aspectly:gap-3">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={isSubmitting || !name.trim()}>
            {isSubmitting ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSave} className="aspectly:space-y-6">
        {error && (
          <div className="aspectly:p-3 aspectly:rounded-lg aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
            {error}
          </div>
        )}
        <div className="aspectly:space-y-2">
          <label
            className="aspectly:text-[11px] aspectly:uppercase aspectly:font-black aspectly:tracking-widest aspectly:text-on-surface-variant aspectly:block"
            htmlFor="edit-campaign-name"
          >
            Campaign Name
          </label>
          <input
            className="aspectly:w-full aspectly:bg-surface-container-low aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-xl aspectly:px-4 aspectly:py-4 aspectly:text-lg aspectly:font-bold aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/20 aspectly:focus:border-primary aspectly:transition-all aspectly:placeholder:text-on-surface-variant/40 aspectly:placeholder:font-normal"
            id="edit-campaign-name"
            placeholder="Summer Collection 2024"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        <div className="aspectly:grid aspectly:grid-cols-1 aspectly:sm:grid-cols-2 aspectly:gap-4">
          <div className="aspectly:space-y-1.5">
            <label
              className="aspectly:text-[10px] aspectly:uppercase aspectly:font-black aspectly:tracking-widest aspectly:text-on-surface-variant aspectly:block"
              htmlFor="edit-start-date"
            >
              Start Date
            </label>
            <div className="aspectly:relative">
              <Icon
                name="calendar_today"
                className="aspectly:absolute aspectly:left-3 aspectly:top-1/2 aspectly:-translate-y-1/2 aspectly:text-on-surface-variant/60 aspectly:text-sm aspectly:pointer-events-none"
              />
              <input
                className="aspectly:w-full aspectly:bg-surface-container-low aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-xl aspectly:pl-10 aspectly:pr-4 aspectly:py-3 aspectly:text-sm aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/20 aspectly:focus:border-primary aspectly:transition-all aspectly:text-on-surface"
                id="edit-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div className="aspectly:space-y-1.5">
            <label
              className="aspectly:text-[10px] aspectly:uppercase aspectly:font-black aspectly:tracking-widest aspectly:text-on-surface-variant aspectly:block"
              htmlFor="edit-end-date"
            >
              End Date
            </label>
            <div className="aspectly:relative">
              <Icon
                name="event_busy"
                className="aspectly:absolute aspectly:left-3 aspectly:top-1/2 aspectly:-translate-y-1/2 aspectly:text-on-surface-variant/60 aspectly:text-sm aspectly:pointer-events-none"
              />
              <input
                className="aspectly:w-full aspectly:bg-surface-container-low aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-xl aspectly:pl-10 aspectly:pr-4 aspectly:py-3 aspectly:text-sm aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/20 aspectly:focus:border-primary aspectly:transition-all aspectly:text-on-surface"
                id="edit-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>

        <div className="aspectly:space-y-2">
          <label
            className="aspectly:text-[11px] aspectly:uppercase aspectly:font-black aspectly:tracking-widest aspectly:text-on-surface-variant aspectly:block"
            htmlFor="edit-objective"
          >
            Primary objective
          </label>
          <div className="aspectly:relative">
            <select
              className="aspectly:w-full aspectly:bg-surface-container-low aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-xl aspectly:pl-4 aspectly:pr-10 aspectly:py-3 aspectly:text-sm aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/20 aspectly:focus:border-primary aspectly:transition-all aspectly:appearance-none aspectly:text-on-surface aspectly:font-medium aspectly:cursor-pointer aspectly:disabled:cursor-not-allowed"
              id="edit-objective"
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              disabled={isSubmitting}
            >
              {OBJECTIVE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Icon
              name="expand_more"
              className="aspectly:absolute aspectly:right-3 aspectly:top-1/2 aspectly:-translate-y-1/2 aspectly:text-on-surface-variant aspectly:pointer-events-none"
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}
